import React, { useState } from "react";
import {
    Box,
    Flex,
    Text,
    Stack,
    Switch,
} from "@chakra-ui/react";
import { useDispatch } from "react-redux";

import ReusableDrawer from "components/Drawers/ReusableDrawer";
import ReusableInput from "components/Inputs/ReusableInput";
import GradientButton from "components/Buttons/GradientButton";

import {
    createProgressionConfigurationAction,
    fetchProgressionConfigurationsAction,
} from "state/actions/agentProgressionActions";

import {
    useCustomNotification,
} from "components/Toasts/OpenNotification";


const AddProgressionConfiguration = ({
    isOpen,
    onClose,
}) => {

    const dispatch = useDispatch();

    const notify =
        useCustomNotification();


    const [loading, setLoading] =
        useState(false);


    const [formData, setFormData] =
        useState({

            level: "",

            minimum_experience_months: 0,

            minimum_package_volume: 0,

            minimum_customer_rating: 0,

            minimum_compliance_score: 0,

            training_completion_required: true,

            is_active: true,

        });


    const handleChange = (
        field,
        value
    ) => {

        setFormData(
            (prev) => ({
                ...prev,
                [field]: value,
            })
        );

    };


    const resetForm = () => {

        setFormData({

            level: "",

            minimum_experience_months: 0,

            minimum_package_volume: 0,

            minimum_customer_rating: 0,

            minimum_compliance_score: 0,

            training_completion_required: true,

            is_active: true,

        });

    };


    const handleClose = () => {

        resetForm();

        onClose();

    };


    const handleSubmit = async () => {

        if (!formData.level.trim()) {

            notify({

                message:
                    "Level is required.",

                type: "error",

            });

            return;

        }


        setLoading(true);


        try {

            const payload = {

                level:
                    formData.level.trim(),

                minimum_experience_months:
                    Number(
                        formData.minimum_experience_months
                    ),

                minimum_package_volume:
                    Number(
                        formData.minimum_package_volume
                    ),

                minimum_customer_rating:
                    Number(
                        formData.minimum_customer_rating
                    ),

                minimum_compliance_score:
                    Number(
                        formData.minimum_compliance_score
                    ),

                training_completion_required:
                    formData.training_completion_required,

                is_active:
                    formData.is_active,

            };


            const response =
                await dispatch(
                    createProgressionConfigurationAction(
                        payload
                    )
                );


            if (response?.success) {

                notify({

                    message:
                        "Progression configuration created successfully.",

                    type: "success",

                });


                dispatch(
                    fetchProgressionConfigurationsAction()
                );


                resetForm();

                onClose();

            } else {

                notify({

                    message:
                        response?.error ||
                        "Failed to create progression configuration.",

                    type: "error",

                });

            }

        } catch (error) {

            notify({

                message:
                    "Failed to create progression configuration.",

                description:
                    error.message,

                type: "error",

            });

        } finally {

            setLoading(false);

        }

    };


    return (

        <ReusableDrawer

            isOpen={isOpen}

            onClose={handleClose}


            headerContent={

                <Flex
                    justifyContent="space-between"
                    alignItems="center"
                    width="100%"
                >

                    <Text
                        fontSize="lg"
                        fontWeight="bold"
                    >
                        Add Progression
                        Configuration
                    </Text>

                </Flex>

            }


            bodyContent={

                <Box
                    width="100%"
                    mt={4}
                >

                    <Stack spacing={5}>

                        {/* LEVEL */}

                        <ReusableInput

                            label="Level"

                            placeholder="e.g. Senior Mogoma Agent"

                            value={
                                formData.level
                            }

                            onChange={(e) =>
                                handleChange(
                                    "level",
                                    e.target.value
                                )
                            }

                        />


                        {/* EXPERIENCE */}

                        <ReusableInput

                            label="Minimum Experience (Months)"

                            type="number"

                            placeholder="e.g. 6"

                            value={
                                formData.minimum_experience_months
                            }

                            onChange={(e) =>
                                handleChange(
                                    "minimum_experience_months",
                                    e.target.value
                                )
                            }

                        />


                        {/* PACKAGE VOLUME */}

                        <ReusableInput

                            label="Minimum Package Volume"

                            type="number"

                            placeholder="e.g. 100"

                            value={
                                formData.minimum_package_volume
                            }

                            onChange={(e) =>
                                handleChange(
                                    "minimum_package_volume",
                                    e.target.value
                                )
                            }

                        />


                        {/* CUSTOMER RATING */}

                        <ReusableInput

                            label="Minimum Customer Rating"

                            type="number"

                            min="0"

                            max="5"

                            step="0.1"

                            placeholder="e.g. 3"

                            value={
                                formData.minimum_customer_rating
                            }

                            onChange={(e) =>
                                handleChange(
                                    "minimum_customer_rating",
                                    e.target.value
                                )
                            }

                        />


                        {/* COMPLIANCE */}

                        <ReusableInput

                            label="Minimum Compliance Score"

                            type="number"

                            min="0"

                            max="100"

                            placeholder="e.g. 90"

                            value={
                                formData.minimum_compliance_score
                            }

                            onChange={(e) =>
                                handleChange(
                                    "minimum_compliance_score",
                                    e.target.value
                                )
                            }

                        />


                        {/* TRAINING */}

                        <Flex
                            alignItems="center"
                            justifyContent="space-between"
                            border="1px"
                            borderColor="gray.300"
                            borderRadius="md"
                            p={4}
                        >

                            <Box>

                                <Text
                                    fontSize="sm"
                                    fontWeight="600"
                                >
                                    Training Completion
                                    Required
                                </Text>

                                <Text
                                    fontSize="xs"
                                    color="gray.500"
                                    mt={1}
                                >
                                    Agent must complete
                                    training before
                                    progressing.
                                </Text>

                            </Box>


                            <Switch

                                isChecked={
                                    formData.training_completion_required
                                }

                                onChange={(e) =>
                                    handleChange(
                                        "training_completion_required",
                                        e.target.checked
                                    )
                                }

                            />

                        </Flex>


                        {/* ACTIVE */}

                        <Flex
                            alignItems="center"
                            justifyContent="space-between"
                            border="1px"
                            borderColor="gray.300"
                            borderRadius="md"
                            p={4}
                        >

                            <Box>

                                <Text
                                    fontSize="sm"
                                    fontWeight="600"
                                >
                                    Active
                                </Text>

                                <Text
                                    fontSize="xs"
                                    color="gray.500"
                                    mt={1}
                                >
                                    Make this progression
                                    configuration active.
                                </Text>

                            </Box>


                            <Switch

                                isChecked={
                                    formData.is_active
                                }

                                onChange={(e) =>
                                    handleChange(
                                        "is_active",
                                        e.target.checked
                                    )
                                }

                            />

                        </Flex>


                        {/* BUTTON */}

                        <Flex
                            justifyContent="flex-end"
                            pt={3}
                        >

                            <GradientButton

                                height="40px"

                                fontSize="sm"

                                onClick={
                                    handleSubmit
                                }

                                isLoading={
                                    loading
                                }

                                size="lg"
                            >
                                Create Configuration
                            </GradientButton>

                        </Flex>

                    </Stack>

                </Box>

            }

        />

    );

};


export default AddProgressionConfiguration;